// ui/src/pages/AuctionPool.jsx
// Neon Stage Showcase — collapsed sets, search, polling, expand all, mobile
// Route: /auction/pool

import { useState, useEffect, useRef, useCallback } from 'react';
import { getRankImage } from '../common/consts';
import { apiService } from '../services/api';
import useStore from '../store/store';
import './AuctionPool.css';

const ROLE_COLORS = {
  duelist:    '#ff4655',
  initiator:  '#FFD166',
  controller: '#a78bfa',
  sentinel:   '#3ce2c2',
  flex:       '#cd94ff',
};
const TIER_COLORS = {
  platinum:  '#9be8e0',
  diamond:   '#f472b6',
  ascendant: '#3fe07f',
  immortal:  '#ff5277',
  radiant:   '#ffd84d',
};
const roleColor = (r) => ROLE_COLORS[(r || '').toLowerCase()] || '#94a3b8';
const tierColor = (t) => TIER_COLORS[(t || '').toLowerCase()] || '#3ce2c2';

function tierSummary(players) {
  const counts = {};
  const order  = ['radiant','immortal','ascendant','diamond','platinum'];
  const sample = {}; // one rank_tier string per tier for image lookup
  players.forEach(p => {
    const t = (p.rank_tier || '').toLowerCase();
    if (t) {
      counts[t] = (counts[t] || 0) + 1;
      if (!sample[t]) sample[t] = p.rank_tier;
    }
  });
  return order
    .filter(t => counts[t])
    .map(t => ({ tier: t, count: counts[t], rank: sample[t] }));
}

function TierSummary({ players }) {
  const summary = tierSummary(players);
  return (
    <span className="pool-set-summary">
      {summary.map((s, i) => (
        <span key={s.tier} className="pool-tier-chip">
          <img src={getRankImage(s.rank)} alt={s.tier} className="pool-tier-img" />
          <span className="pool-tier-count">{s.count}</span>
          {i < summary.length - 1 && <span className="pool-tier-sep">·</span>}
        </span>
      ))}
    </span>
  );
}

const POLL_INTERVAL = 30000;

// ── Flip to true on auction day ────────────────────────────────
const POOL_LIVE = true; // false = Coming Soon, true = show pool (active or completed)

// ── Coming Soon screen ─────────────────────────────────────────
function PoolComingSoon() {
  return (
    <div className="pool-coming-soon">
      <div className="pool-bg" aria-hidden="true">
        {[...Array(5)].map((_, i) => <div key={i} className="pool-bg-orb" style={{ '--i': i }} />)}
      </div>
      <div className="pool-coming-content">
        <div className="pool-coming-eyebrow">VPL · SEASON 5</div>
        <h1 className="pool-coming-title">AUCTION POOL</h1>
        <p className="pool-coming-sub">Player pool will be revealed on auction day</p>
        <div className="pool-coming-date">AUG 7 · 2026</div>
      </div>
    </div>
  );
}

// ── Auction Ended screen ───────────────────────────────────────
function PoolEnded() {
  return (
    <div className="pool-coming-soon">
      <div className="pool-bg" aria-hidden="true">
        {[...Array(5)].map((_, i) => <div key={i} className="pool-bg-orb" style={{ '--i': i }} />)}
      </div>
      <div className="pool-coming-content">
        <div className="pool-coming-eyebrow">VPL · SEASON 5</div>
        <h1 className="pool-coming-title">AUCTION COMPLETE</h1>
        <p className="pool-coming-sub">The Season 5 auction has ended</p>
        <a href="/auction/results" className="pool-coming-results-btn">VIEW RESULTS →</a>
      </div>
    </div>
  );
}

function AuctionPool() {
  const [pool, setPool]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [search, setSearch]     = useState('');
  const [expanded, setExpanded] = useState(new Set());
  const [allOpen, setAllOpen]   = useState(false);
  const prevStatusRef           = useRef(null);
  const setRefs                 = useRef({});
  const pollRef                 = useRef(null);

  const applyPool = useCallback((data, isUpdate = false) => {
    const prevStatus = prevStatusRef.current;
    const newStatus  = data.session_status;

    if (isUpdate && prevStatus !== 'active' && newStatus === 'active') {
      const liveNum = data.sets?.find(s => s.set_number === data.current_set)?.set_number;
      setExpanded(liveNum ? new Set([liveNum]) : new Set());
      setAllOpen(false);
    }

    if (isUpdate && newStatus === 'active' && prevStatusRef.current === 'active') {
      setExpanded(prev => {
        const newLive = data.current_set;
        if (!newLive) return prev;
        const next = new Set(prev);
        next.add(newLive);
        return next;
      });
    }

    prevStatusRef.current = newStatus;
    setPool(data);

    if (!isUpdate && newStatus === 'active' && data.current_set) {
      setExpanded(new Set([data.current_set]));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadPool = useCallback(async (seasonId, isUpdate = false) => {
    try {
      const res = await apiService.getAuctionPool(seasonId);
      if (res.data?.error) {
        if (!isUpdate) setError('The auction pool will appear here once the auction is set up.');
        return { shouldPoll: false };
      } else {
        const status = res.data?.session_status;
        if (status === 'completed') clearTimeout(pollRef.current);
        applyPool(res.data, isUpdate);
        return { shouldPoll: status === 'active' };
      }
    } catch (err) {
      if (!isUpdate) setError(err?.response?.data?.Message || 'Failed to load the auction pool');
      return { shouldPoll: false };
    } finally {
      if (!isUpdate) setLoading(false);
    }
  }, [applyPool]);

  useEffect(() => {
    let mounted = true;

    const scheduleNext = (seasonId) => {
      pollRef.current = setTimeout(async () => {
        if (!mounted) return;
        let shouldPoll = false;
        if (document.visibilityState === 'visible') {
          const result = await loadPool(seasonId, true);
          shouldPoll = result?.shouldPoll ?? false;
        } else {
          // Tab hidden — check again later only if was active
          shouldPoll = prevStatusRef.current === 'active';
        }
        if (mounted && shouldPoll) scheduleNext(seasonId);
      }, POLL_INTERVAL);
    };

    const init = (seasonId) => {
      loadPool(seasonId, false).then((result) => {
        if (mounted && result?.shouldPoll) scheduleNext(seasonId);
      });

      const onVisible = () => {
        if (document.visibilityState === 'visible') {
          clearTimeout(pollRef.current);
          loadPool(seasonId, true).then((result) => {
            if (mounted && result?.shouldPoll) scheduleNext(seasonId);
          });
        }
      };
      document.addEventListener('visibilitychange', onVisible);
      pollRef._cleanup = () => document.removeEventListener('visibilitychange', onVisible);
    };

    const state = useStore.getState();
    if (state.currentSeason?.id) {
      init(state.currentSeason.id);
    } else {
      state.fetchSeasons();
      const unsubscribe = useStore.subscribe((s) => {
        if (s.currentSeason?.id) { unsubscribe(); init(s.currentSeason.id); }
      });
    }

    return () => {
      mounted = false;
      clearTimeout(pollRef.current);
      if (pollRef._cleanup) pollRef._cleanup();
    };
  }, [loadPool]);

  useEffect(() => {
    if (!pool || !search.trim()) return;
    const q = search.trim().toLowerCase();
    const matchingSets = pool.sets
      .filter(s => s.players.some(p => (p.game_name || '').toLowerCase().includes(q)))
      .map(s => s.set_number);
    if (matchingSets.length > 0) {
      setExpanded(new Set(matchingSets));
      setAllOpen(false);
      // Don't auto-scroll — YOU'RE HERE tag and expand is enough
      // Auto-scroll was locking viewport and breaking mouse scroll
    }
  }, [search, pool]);

  const toggleSet = (setNum) => {
    // Clear search when manually toggling sets so matchIdx doesn't interfere
    if (search) {
      setSearch('');
      setExpanded(new Set([setNum]));
      setAllOpen(false);
      return;
    }
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(setNum)) next.delete(setNum);
      else next.add(setNum);
      return next;
    });
  };

  const toggleAll = () => {
    if (allOpen) {
      setExpanded(new Set());
      setAllOpen(false);
    } else {
      // Stagger opening 60ms apart to avoid rendering all at once
      pool.sets.forEach((s, i) => {
        setTimeout(() => {
          setExpanded(prev => new Set([...prev, s.set_number]));
        }, i * 60);
      });
      setAllOpen(true);
    }
  };

  // Coming Soon — flip POOL_LIVE to true on auction day
  if (!POOL_LIVE) return <PoolComingSoon />;

  // Auction ended — stop showing pool, redirect to results
  if (pool?.session_status === 'completed') return <PoolEnded />;

  if (loading) {
    return (
      <div className="pool-loading">
        <div className="pool-spinner" />
        <p>Loading auction pool…</p>
      </div>
    );
  }

  if (error || !pool) {
    return (
      <div className="pool-empty">
        <h2>AUCTION POOL</h2>
        <p>{error || 'No pool data available yet.'}</p>
      </div>
    );
  }

  const q        = search.trim().toLowerCase();
  const isActive = pool.session_status === 'active';

  return (
    <div className="pool-root">
      <div className="pool-bg" aria-hidden="true">
        {[...Array(5)].map((_, i) => <div key={i} className="pool-bg-orb" style={{ '--i': i }} />)}
      </div>

      <div className="pool-search-sticky">
        <div className="pool-sticky-inner">
          <div className="pool-search-wrap">
            <span className="pool-search-icon">⌕</span>
            <input
              className="pool-search"
              placeholder="Search your name to find your set…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && (
              <button className="pool-search-clear" onClick={() => {
                setSearch('');
                setExpanded(new Set());
                setAllOpen(false);
              }}>✕</button>
            )}
          </div>
          <button className="pool-expand-btn" onClick={toggleAll}>
            {allOpen ? 'COLLAPSE ALL' : 'EXPAND ALL'}
          </button>
        </div>
      </div>

      <header className="pool-hero">
        <div className="pool-hero-eyebrow">VPL · SEASON 5</div>
        <h1 className="pool-title">AUCTION POOL</h1>
        <p className="pool-subtitle">
          {pool.total_players} verified players · {pool.sets.length} sets
          {isActive && <span className="pool-live"> · LIVE NOW</span>}
        </p>
      </header>

      <div className="pool-sets">
        {pool.sets.map((s) => {
          const isLive   = s.set_number === pool.current_set && isActive;
          const isDone   = s.status === 'completed';
          const isOpen   = expanded.has(s.set_number);
          const totalPts = s.players.reduce((acc, p) => acc + (p.base_price || 0), 0);
          const hasMatch = q
            ? s.players.some(p => (p.game_name||'').toLowerCase().includes(q))
            : false;
          const matchIdx = hasMatch
            ? s.players.map((p,i) => (p.game_name||'').toLowerCase().includes(q) ? i : -1).filter(i => i >= 0)
            : [];

          return (
            <div
              key={s.set_number}
              className={`pool-set ${isLive ? 'pool-set-live' : ''} ${isDone ? 'pool-set-done' : ''} ${isOpen ? 'pool-set-open' : ''}`}
              ref={el => setRefs.current[s.set_number] = el}
            >
              <button className="pool-set-header" onClick={() => toggleSet(s.set_number)} aria-expanded={isOpen}>
                <div className="pool-set-header-left">
                  {isLive && <span className="pool-set-live-dot" aria-hidden="true" />}
                  <span className="pool-set-num">SET {s.set_number}</span>
                  <span className="pool-set-pipe" aria-hidden="true" />
                  <span className="pool-set-count">{s.players.length} PLAYERS</span>
                  <span className="pool-set-pipe" aria-hidden="true" />
                  <span className="pool-set-pts">{totalPts} PTS</span>
                  <TierSummary players={s.players} />
                </div>
                <div className="pool-set-header-right">
                  {isLive && <span className="pool-tag pool-tag-live">● LIVE</span>}
                  {isDone && <span className="pool-tag pool-tag-done">✓ DONE</span>}
                  {matchIdx.length > 0 && <span className="pool-tag pool-tag-match">YOU'RE HERE</span>}
                  <span className={`pool-chevron ${isOpen ? 'pool-chevron-open' : ''}`}>›</span>
                </div>
              </button>

              {isOpen && (
                <div className="pool-set-body">
                  <SetCarousel
                    key={`set-${s.set_number}-${matchIdx.join(',')}`}
                    players={s.players}
                    matchIdx={matchIdx}
                    setNumber={s.set_number}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function SetCarousel({ players, matchIdx, setNumber }) {
  const trackRef = useRef(null);
  const initIdx  = matchIdx.length > 0 ? matchIdx[0] : 0;
  const [activeIdx, setActiveIdx] = useState(initIdx);
  const [canLeft, setCanLeft]     = useState(initIdx > 0);
  const [canRight, setCanRight]   = useState(players.length > 1);

  const CARD_W    = 180;
  const GAP       = 16;
  const TRACK_PAD = 32; // matches carousel-track padding-left

  const scrollTo = useCallback((idx, smooth = true) => {
    const clamped = Math.max(0, Math.min(players.length - 1, idx));
    setActiveIdx(clamped);
    setCanLeft(clamped > 0);
    setCanRight(clamped < players.length - 1);
    const el = trackRef.current;
    if (!el || el.clientWidth === 0) return;
    const outerW     = el.clientWidth;
    const cardCenter = TRACK_PAD + clamped * (CARD_W + GAP) + CARD_W / 2;
    const offset     = cardCenter - outerW / 2;
    el.scrollTo({ left: Math.max(0, offset), behavior: smooth ? 'smooth' : 'instant' });
  }, [players.length]);

  // Re-center after DOM settles — try multiple times to handle animation delay
  useEffect(() => {
    const attempts = [80, 200, 400];
    const timers = attempts.map(delay =>
      setTimeout(() => scrollTo(initIdx, false), delay)
    );
    return () => timers.forEach(clearTimeout);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);



  return (
    <div className="carousel-wrap">
      <div className="carousel-spotlights" aria-hidden="true">
        {players.slice(0, 8).map((p, i) => (
          <div key={i} className="carousel-beam"
            style={{ '--tier': tierColor(p.rank_tier), '--si': i, '--total': Math.min(players.length, 8) }} />
        ))}
      </div>

      <div className="carousel-track-outer" ref={trackRef} onWheel={(e) => {
        // Only capture horizontal scroll — pass vertical scroll to page
        if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
          e.currentTarget.style.overflowX = 'hidden';
          setTimeout(() => { if (e.currentTarget) e.currentTarget.style.overflowX = 'auto'; }, 50);
        }
      }}>
        <div className="carousel-track">
{players.map((p, i) => (
            <PoolCard
              key={i} player={p}
              isActive={i === activeIdx}
              isMatch={matchIdx.includes(i)}
              playerNum={i + 1}
              setNumber={setNumber}
              onClick={() => scrollTo(i)}
            />
          ))}
        </div>
      </div>

      <div className="carousel-floor" aria-hidden="true" />

      <div className="carousel-controls">
        <button className={`carousel-nav ${!canLeft ? 'carousel-nav-off' : ''}`}
          onClick={() => scrollTo(activeIdx - 1)} disabled={!canLeft} aria-label="Previous">‹</button>
        <span className="carousel-counter">{activeIdx + 1} / {players.length}</span>
        <button className={`carousel-nav ${!canRight ? 'carousel-nav-off' : ''}`}
          onClick={() => scrollTo(activeIdx + 1)} disabled={!canRight} aria-label="Next">›</button>
      </div>
    </div>
  );
}

function PoolCard({ player, isActive, isMatch, playerNum, setNumber, onClick }) {
  const tier    = tierColor(player.rank_tier);
  const role    = roleColor(player.role);
  const secRole = roleColor(player.sec_role);
  const sold    = player.status === 'sold';

  // Show the rank string (with sub-number) that matches the pricing tier.
  // e.g. rank_tier="immortal", peak_rank="Immortal 2" → "IMMORTAL 2"
  const pricingTier = (player.rank_tier || '').toLowerCase();
  const rank = (() => {
    if (player.peak_rank && player.peak_rank.toLowerCase().includes(pricingTier)) return player.peak_rank.toUpperCase();
    if (player.cur_rank && player.cur_rank.toLowerCase().includes(pricingTier)) return player.cur_rank.toUpperCase();
    return pricingTier.toUpperCase();
  })();

  return (
    <div
      className={`pc ${isActive ? 'pc-active' : ''} ${isMatch ? 'pc-match' : ''} ${sold ? 'pc-sold' : ''} ${player.status === 'unsold' ? 'pc-unsold-card' : ''}`}
      style={{ '--tier': tier, '--role': role }}
      onClick={onClick}
    >
      {isActive && <div className="pc-beam" aria-hidden="true" />}
      <span className="pc-br pc-br-tl" /><span className="pc-br pc-br-tr" />
      <span className="pc-br pc-br-bl" /><span className="pc-br pc-br-br" />
      <div className="pc-roleband" />

      {isMatch && <div className="pc-match-badge">SET {setNumber} · #{playerNum}</div>}

      <div className="pc-avatar-wrap">
        <div className="pc-avatar-ring">
          {player.photo
            ? <img src={player.photo} alt={player.game_name} className="pc-avatar-img" loading="lazy" />
            : <div className="pc-avatar-ph">{(player.game_name || '?')[0].toUpperCase()}</div>
          }
        </div>
      </div>

      <div className="pc-name">{player.game_name}</div>

      <div className="pc-roles-row">
        {player.role && (
          <span className="pc-role-badge" style={{ color: role, borderColor: `${role}44` }}>
            {player.role.toUpperCase()}
          </span>
        )}
        {player.sec_role && player.sec_role !== player.role && (
          <span className="pc-role-badge pc-role-sec" style={{ color: secRole, borderColor: `${secRole}33` }}>
            {player.sec_role.toUpperCase()}
          </span>
        )}
      </div>

      {rank && (
        <div className="pc-rank-row">
          <img src={getRankImage(player.rank_tier)} alt={rank} className="pc-rank-img" />
          <span className="pc-rank-text">{rank}</span>
        </div>
      )}

      <div className="pc-divider" aria-hidden="true" />

      {sold ? (
        <div className="pc-sold-banner">
          <div className="pc-sold-top">
            <div className="pc-sold-check">✓</div>
            <span className="pc-sold-label-text">SOLD</span>
            <span className="pc-sold-amount">{player.final_price}<small> PTS</small></span>
          </div>
          <span className="pc-sold-team-name">{player.sold_to || '—'}</span>
        </div>
      ) : player.status === 'unsold' ? (
        <div className="pc-unsold-banner">
          <div className="pc-unsold-x">✕</div>
          <span className="pc-unsold-label-text">UNSOLD</span>
        </div>
      ) : (
        <div className="pc-price-section">
          <span className="pc-price-label">BASE PRICE</span>
          <span className="pc-price" style={{ color: tier }}>{player.base_price}<small> PTS</small></span>
        </div>
      )}

      <div className="pc-podium" aria-hidden="true">
        <div className="pc-podium-top" />
        <div className="pc-podium-body" />
        <div className="pc-podium-glow" />
      </div>

      <div className="pc-smoke pc-smoke-l" aria-hidden="true" />
      <div className="pc-smoke pc-smoke-r" aria-hidden="true" />
    </div>
  );
}

export default AuctionPool;